/**
 * Import a venue's printed seating chart from a photo.
 *
 * The detection is classical image processing — no model, no guesswork the
 * planner cannot see. Which is exactly why this dialog leads with
 * the annotated photo: an automatic import is only worth having if you can check
 * it in two seconds, so every table the detector accepted comes back outlined
 * and numbered, and everything it skipped comes back greyed.
 *
 * Nothing is written until "Add to plan". The result then lands through the
 * ordinary edit path, so ⌘Z undoes the whole import in one step.
 */

import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'

import { api } from '../lib/api'
import { LIMITS } from '../lib/config'
import { MIN_SEATS, clampSeatCount, maxSeatCount } from '../lib/geometry'
import { useT } from '../lib/i18n/I18nProvider'
import { messageForError } from '../lib/i18n/errors'
import type { FloorPlanDetection, Plan, TableShape } from '../lib/types'
import { usePlanStore } from '../store/planStore'

interface Props {
  open: boolean
  onClose: () => void
  plan: Plan
}

type Stage = 'pick' | 'reading' | 'review'

export function FloorPlanImportDialog({ open, onClose, plan }: Props) {
  const t = useT()
  const [stage, setStage] = useState<Stage>('pick')
  const [detection, setDetection] = useState<FloorPlanDetection | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  // Name HEIC only where it opens: telling a Chrome user their iPhone photo is
  // fine, and then refusing it, is worse than not mentioning it.
  const [readsHeic, setReadsHeic] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  /** Bumped by every read and by closing, so a late answer cannot land on a later visit. */
  const attempt = useRef(0)

  const importDetectedTables = usePlanStore((s) => s.importDetectedTables)
  const showToast = usePlanStore((s) => s.showToast)

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // The photo reader (OpenCV, ~13 MB) starts loading as the dialog opens, so the
  // photo picked a few seconds later usually finds it ready; closing the dialog
  // gives its memory back.
  useEffect(() => {
    if (!open) return undefined
    void api.canReadHeic().then(setReadsHeic)
    api.prepareFloorPlanImport()
    return () => {
      attempt.current += 1
      api.releaseFloorPlanImport()
    }
  }, [open])

  // Reset on close rather than on open, so the closing animation doesn't play
  // over a dialog that has already snapped back to its empty state.
  useEffect(() => {
    if (open) return
    const timer = window.setTimeout(() => {
      setStage('pick')
      setDetection(null)
      setError(null)
    }, 220)
    return () => window.clearTimeout(timer)
  }, [open])

  const read = async (file: File) => {
    const current = ++attempt.current
    setError(null)
    setStage('reading')
    try {
      const result = await api.importFloorPlan(plan.id, file)
      if (current !== attempt.current) return
      setDetection(result)
      setStage('review')
    } catch (err) {
      if (current !== attempt.current) return
      setError(messageForError(t, err, t.errors.photoUnreadable))
      setStage('pick')
    }
  }

  const onPick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    // Clear first: picking the same file twice must still fire a change event.
    event.target.value = ''
    if (file) void read(file)
  }

  const onDrop = (event: React.DragEvent) => {
    event.preventDefault()
    setDragging(false)
    const file = event.dataTransfer.files?.[0]
    if (file) void read(file)
  }

  /**
   * @param seatsByShape chairs to give the tables the sheet drew none around,
   *   one number per shape. Plenty of charts are schematics — bare numbered
   *   circles — and without this every one of them lands on the shape's
   *   default, leaving the planner to fix thirty tables one at a time. Per
   *   shape rather than one number for the lot, because a hall's round guest
   *   tables and its long head table never seat the same number.
   * @param overwriteCounted also restate the chairs the detector *did* read off
   *   the drawing. Off by default: evidence from the sheet beats a default.
   */
  const apply = (
    mode: 'replace' | 'add',
    seatsByShape: Record<TableShape, number>,
    overwriteCounted: boolean,
  ) => {
    if (!detection) return
    const tables = detection.tables.map((table) =>
      table.chairsCounted && !overwriteCounted
        ? table
        : { ...table, seatCount: seatsByShape[table.shape] },
    )
    const added = importDetectedTables(tables, mode)
    showToast(
      mode === 'replace' ? t.importDialog.roomReplaced(added) : t.importDialog.tablesAdded(added),
      'success',
    )
    onClose()
  }

  const maxMb = LIMITS.maxUploadMb

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.16 }}
          onClick={onClose}
          role="presentation"
        >
          <motion.div
            className="modal modal--wide"
            initial={{ opacity: 0, scale: 0.94, y: 14 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ type: 'spring', stiffness: 420, damping: 32 }}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="import-title"
          >
            <button type="button" className="modal-close" onClick={onClose} aria-label={t.common.close}>
              ×
            </button>

            <span className="modal-eyebrow">{t.importDialog.eyebrow}</span>
            <h2 id="import-title" className="modal-title">
              {stage === 'review' ? t.importDialog.reviewTitle : t.importDialog.startTitle}
            </h2>

            {stage !== 'review' && (
              <>
                <p className="modal-lede">{t.importDialog.lede}</p>

                <div
                  className={`dropzone${dragging ? ' is-over' : ''}${
                    stage === 'reading' ? ' is-busy' : ''
                  }`}
                  onDragOver={(event) => {
                    event.preventDefault()
                    setDragging(true)
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={onDrop}
                  onClick={() => stage !== 'reading' && fileRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') fileRef.current?.click()
                  }}
                >
                  {stage === 'reading' ? (
                    <>
                      <motion.span
                        className="loader-ring loader-ring--sm"
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1.1, ease: 'linear' }}
                      />
                      <strong>{t.importDialog.reading}</strong>
                      <small>{t.importDialog.readingBlurb}</small>
                    </>
                  ) : (
                    <>
                      <SheetIcon />
                      <strong>{t.importDialog.dropPhoto}</strong>
                      <small>
                        {readsHeic
                          ? t.importDialog.fileTypesWithHeic(maxMb)
                          : t.importDialog.fileTypes(maxMb)}
                      </small>
                    </>
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={onPick}
                />

                <ul className="import-tips">
                  {t.importDialog.tips.map((tip) => (
                    <li key={tip}>{tip}</li>
                  ))}
                </ul>
              </>
            )}

            {stage === 'review' && detection && (
              <Review
                detection={detection}
                existing={plan.tables.length}
                defaults={plan.settings.defaultSeats}
                onRetry={() => setStage('pick')}
                onApply={apply}
              />
            )}

            {error && <p className="modal-error">{error}</p>}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

interface ReviewProps {
  detection: FloorPlanDetection
  existing: number
  /** The plan's own table defaults, which the steppers start from. */
  defaults: Record<TableShape, number>
  onRetry: () => void
  onApply: (
    mode: 'replace' | 'add',
    seatsByShape: Record<TableShape, number>,
    overwriteCounted: boolean,
  ) => void
}

/** Shapes the detector can actually produce. It never proposes a sweetheart. */
const DETECTED_SHAPES: TableShape[] = ['round', 'square', 'rectangle']

function Review({ detection, existing, defaults, onRetry, onApply }: ReviewProps) {
  const t = useT()
  const { tableCount, roundCount, squareCount, rectCount, seatTotal, chairsCounted, ignored } =
    detection
  const uncounted = tableCount - chairsCounted
  const [seats, setSeats] = useState<Record<TableShape, number>>(() => ({ ...defaults }))
  const [overwriteCounted, setOverwriteCounted] = useState(false)

  // How many detected tables each stepper governs — a control that does not say
  // what it affects is a control nobody trusts.
  const governed = (shape: TableShape) =>
    detection.tables.filter(
      (table) => table.shape === shape && (overwriteCounted || !table.chairsCounted),
    ).length

  if (tableCount === 0) {
    return (
      <>
        <p className="modal-lede">
          {ignored > 0 ? t.importDialog.noneRepeat(ignored) : t.importDialog.noneFound}
        </p>
        <img className="import-preview" src={detection.previewImage} alt={t.importDialog.sheetAlt} />
        <div className="modal-actions">
          <button type="button" className="btn btn-primary modal-cta" onClick={onRetry}>
            {t.importDialog.tryAnother}
          </button>
        </div>
      </>
    )
  }

  return (
    <>
      <p className="modal-lede">{t.importDialog.reviewLede(tableCount)}</p>

      <img
        className="import-preview"
        src={detection.previewImage}
        alt={t.importDialog.sheetAlt}
      />

      <dl className="import-facts">
        <Fact label={t.importDialog.factRound} value={String(roundCount)} />
        <Fact label={t.importDialog.factRectangular} value={String(squareCount + rectCount)} />
        <Fact label={t.importDialog.factSeats} value={String(seatTotal)} />
        <Fact label={t.importDialog.factSkipped} value={String(ignored)} />
      </dl>

      <p className="import-note">
        {uncounted === 0
          ? t.importDialog.chairsRead
          : chairsCounted === 0
            ? t.importDialog.noChairsDrawn
            : t.importDialog.countedPartial(chairsCounted, tableCount)}
        {detection.truncated ? ` ${t.importDialog.truncated}` : ''}
      </p>

      {(uncounted > 0 || overwriteCounted) && (
        <div className="import-seats">
          <span className="label">{t.importDialog.seatsForUncounted}</span>
          <ul className="settings-rows">
            {DETECTED_SHAPES.filter((shape) => governed(shape) > 0).map((shape) => (
              <li key={shape} className="settings-row">
                <span className="settings-row-name">{t.shape[shape]}</span>
                <span className="settings-row-note">
                  {t.importDialog.governs(governed(shape))}
                </span>
                <div className="stepper stepper--sm">
                  <button
                    type="button"
                    className="stepper-btn"
                    onClick={() =>
                      setSeats((current) => ({
                        ...current,
                        [shape]: clampSeatCount(current[shape] - 1, shape),
                      }))
                    }
                    disabled={seats[shape] <= MIN_SEATS}
                    aria-label={t.inspector.removeChair}
                  >
                    −
                  </button>
                  <span className="stepper-value">
                    <strong>{seats[shape]}</strong>
                  </span>
                  <button
                    type="button"
                    className="stepper-btn"
                    onClick={() =>
                      setSeats((current) => ({
                        ...current,
                        [shape]: clampSeatCount(current[shape] + 1, shape),
                      }))
                    }
                    disabled={seats[shape] >= maxSeatCount(shape)}
                    aria-label={t.inspector.addChair}
                  >
                    +
                  </button>
                </div>
              </li>
            ))}
          </ul>
          {chairsCounted > 0 && (
            <label className="import-overwrite">
              <input
                type="checkbox"
                checked={overwriteCounted}
                onChange={(event) => setOverwriteCounted(event.target.checked)}
              />
              <span>{t.importDialog.overwriteCounted(chairsCounted)}</span>
            </label>
          )}
        </div>
      )}

      <div className="modal-actions">
        <button
          type="button"
          className="btn btn-primary modal-cta"
          onClick={() => onApply('replace', seats, overwriteCounted)}
        >
          {existing > 0 ? t.importDialog.replaceExisting(existing) : t.importDialog.addTables}
        </button>
        {existing > 0 && (
          <button type="button" className="btn btn-outline" onClick={() => onApply('add', seats, overwriteCounted)}>
            {t.importDialog.keepAndAdd}
          </button>
        )}
        <button type="button" className="btn btn-ghost" onClick={onRetry}>
          {t.importDialog.tryAnother}
        </button>
      </div>
    </>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="import-fact">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  )
}

function SheetIcon() {
  return (
    <svg className="dropzone-icon" viewBox="0 0 44 44" aria-hidden="true">
      <rect
        x="8"
        y="5"
        width="28"
        height="34"
        rx="3"
        fill="#F4F8FC"
        stroke="#9DBBD9"
        strokeWidth="1.6"
      />
      <circle cx="17" cy="16" r="4.4" fill="none" stroke="#7FA6D0" strokeWidth="1.4" />
      <circle cx="28" cy="16" r="4.4" fill="none" stroke="#7FA6D0" strokeWidth="1.4" />
      <rect
        x="13"
        y="25"
        width="18"
        height="8"
        rx="1.6"
        fill="none"
        stroke="#D0977B"
        strokeWidth="1.4"
      />
    </svg>
  )
}
